import { ApiCollection }                        from 'Collections/ApiCollection';
import ApeCodeModel                             from 'Models/directory/ApeCodeModel';
import CountryEnterprisePropertyModel           from 'Models/directory/CountryEnterprisePropertyModel';
import EnterpriseCountryEnterprisePropertyModel from 'Models/directory/EnterpriseCountryEnterprisePropertyModel';
import EnterpriseGroupModel                     from 'Models/directory/EnterpriseGroupModel';
import EnterpriseModel                          from 'Models/directory/EnterpriseModel';
import EnterprisePropertyModel                  from 'Models/directory/EnterprisePropertyModel';
import NafCodeModel                             from 'Models/directory/NafCodeModel';
import InvoicingOwnerModel                      from 'Models/invoice/InvoicingOwnerModel';
import TaxZoneModel                             from 'Models/invoice/TaxZoneModel';
import EnterpriseConfigModel                    from 'Models/sales/EnterpriseConfigModel';
import By                                       from 'components/By';
import { IGridInfoItem }                        from 'components/GridInfo/GridInfo';
import React                                    from 'react';
import AbstractModelXStore                      from 'stores/AbstractModelXStore';
import { chunkArray }                           from 'tools/arrayTools';
import { whenAsync }                            from 'tools/modelxTools';
import { formatByInterval }                     from 'tools/stringTools';

type GetEnterprisePropertyValue = {
	(
		enterpriseUrn: Urn,
		reference: DirectoryEnterprisePropertyReference,
	): string | undefined;
	(
		enterpriseUrn: Urn,
		reference: DirectoryEnterprisePropertyReference,
		mode: 'raw'
	): string | undefined;
	(
		enterpriseUrn: Urn,
		reference: DirectoryEnterprisePropertyReference,
		mode: 'formatted'
	): React.ReactElement | string | undefined;
};

export default class EnterprisePropertiesStore extends AbstractModelXStore {
	private apeCodeCollection = new ApiCollection(ApeCodeModel);
	private countryEnterprisePropertyCollection = new ApiCollection(CountryEnterprisePropertyModel);
	private enterpriseCollection = new ApiCollection(EnterpriseModel);
	private enterpriseConfigCollection = new ApiCollection(EnterpriseConfigModel);
	private enterpriseCountryEnterprisePropertyCollection = new ApiCollection(EnterpriseCountryEnterprisePropertyModel);
	private enterprisePropertyCollection = new ApiCollection(EnterprisePropertyModel);
	private invoicingOwnerCollection = new ApiCollection(InvoicingOwnerModel);
	private loadedCountryEnterprisePropertyIds: string[] = [];
	private loadedEnterpriseForGridInfoIds: string[] = [];
	private loadedEnterpriseIds: string[] = [];
	private loadingEnterpriseForGridInfoIds: string[] = [];
	private loadingEnterpriseIds: string[] = [];
	private nafCodeCollection = new ApiCollection(NafCodeModel);

	public getEnterpriseGridInfoData = (enterpriseUrn: Urn, numberOfLines?: number) => {
		const enterprise = this.enterpriseCollection.find(e => e.urn === enterpriseUrn);

		if (!enterprise) {
			return [];
		}

		const invoicingOwner = this.invoicingOwnerCollection
			.find(io => io.getUrn('enterprise') === enterpriseUrn);
		const enterpriseConfig = this.enterpriseConfigCollection
			.find(ec => ec.enterpriseUrn === enterpriseUrn);

		const defaultGridInfoData: IGridInfoItem[] = [
			{
				label: `Pays`,
				type: 'country',
				value: enterprise.country,
			},
			{
				label: `Nom commercial`,
				value: enterprise.commercialName,
			},
			{
				label: `${EnterpriseGroupModel.staticLabel}`,
				loading: enterprise.enterpriseGroup.isLoading,
				value: enterprise.enterpriseGroup.name,
			},
			{
				label: `Type`,
				loading: enterprise.enterpriseType.isLoading,
				value: enterprise.enterpriseType.label,
			},
			{
				label: `Code comptable`,
				value: invoicingOwner?.accountingCode || enterprise.reference,
			},
			{
				label: TaxZoneModel.staticLabel,
				loading: invoicingOwner?.taxZone?.isLoading,
				value: invoicingOwner?.taxZone?.label,
			},
			{
				label: 'Contrats sur bon de commande',
				type: 'boolean',
				value: enterpriseConfig?.onPurchaseOrder,
			},
			{
				label: 'Devis sur bon de commande',
				type: 'boolean',
				value: enterpriseConfig?.quotationsOnPurchaseOrder,
			},
		];

		const propertiesGridInfoData = this.getEnterprisePropertiesGridInfoData(enterpriseUrn);
		const createdByGridInfoData: IGridInfoItem[] = [{ label: 'Entreprise créée', value: <By model={enterprise} /> }];

		const finalInfoData: IGridInfoItem[] = [...defaultGridInfoData, ...propertiesGridInfoData, ...createdByGridInfoData];
		if (numberOfLines) {
			return chunkArray(finalInfoData, numberOfLines);
		}
		return [finalInfoData];
	};

	public getEnterprisePropertiesGridInfoData = (enterpriseUrn: Urn) => {
		const gridInfoData: IGridInfoItem[] = [];

		const enterpriseEceps = this.enterpriseCountryEnterprisePropertyCollection
			.filter(ecep => ecep.getUrn('enterprise') === enterpriseUrn);

		enterpriseEceps.forEach(ecep => {
			const countryEnterpriseProperty = this.countryEnterprisePropertyCollection
				.find(cep => cep.id === ecep.getId('countryEnterpriseProperty'));
			const enterpriseProperty = this.enterprisePropertyCollection
				.find(ep => ep.id === countryEnterpriseProperty?.getId('enterpriseProperty'));

			const value = enterpriseProperty
				? this.formatValue(ecep.value, enterpriseProperty.reference)
				: undefined;

			gridInfoData.push({
				label: enterpriseProperty?.label,
				value: value,
			});
		});

		return gridInfoData;
	};

	// eslint-disable-next-line @typescript-eslint/member-ordering
	public getEnterprisePropertyValue: GetEnterprisePropertyValue = ((
		enterpriseUrn: Urn,
		reference: DirectoryEnterprisePropertyReference,
		mode: 'raw' | 'formatted' = 'raw',
	) => {
		const enterpriseProperty = this.enterprisePropertyCollection
			.find(ep => ep.reference === reference);

		const enterpriseEceps = this.enterpriseCountryEnterprisePropertyCollection
			.filter(ecep => ecep.getUrn('enterprise') === enterpriseUrn);

		const value = enterpriseEceps.find(ecep => {
			return this.countryEnterprisePropertyCollection
				.some(cep =>
					ecep.getId('countryEnterpriseProperty') === cep.id &&
					cep.getId('enterpriseProperty') === enterpriseProperty?.id,
				);
		})?.value;

		if (!value) {
			return undefined;
		}

		if (mode === 'raw') {
			return value;
		}

		return this.formatValue(value, reference);
	}) as GetEnterprisePropertyValue;

	public getIdentificationPropertyValue = (enterpriseUrn: Urn) => {
		let value: string|undefined = undefined;
		const idn = this.getEnterprisePropertyValue(enterpriseUrn, 'identification_number', 'formatted');
		if (idn) {
			value = `ID - ${idn}`;
		}

		const siren = this.getEnterprisePropertyValue(enterpriseUrn, 'siren', 'formatted');
		if (siren) {
			value = `SIREN - ${siren}`;
		}

		const vat = this.getEnterprisePropertyValue(enterpriseUrn, 'intra_community_vat', 'formatted');
		if (vat) {
			value = `N°TVA - ${vat}`;
		}

		return value;
	};

	public async initAsync(enterpriseIds: id[], setForGridInfo?: boolean) {
		this.clear();

		this.loadingEnterpriseIds = this.idsToStringArray(enterpriseIds);
		this.setIsLoading(true);

		if (setForGridInfo) {
			this.loadingEnterpriseForGridInfoIds = this.idsToStringArray(enterpriseIds);
			await this.enterpriseCollection.listBy(enterpriseIds, 'id');

			await Promise.all([
				...this.enterpriseCollection.map(async (e) => {
					if (e.getId('enterpriseGroup')) {
						return whenAsync(() => e.enterpriseGroup.isLoaded);
					}
				}),
				this.enterpriseCollection.whenIsLoaded(e => e.enterpriseType),
				this.enterpriseConfigCollection
					.listBy((this.enterpriseCollection.urns as Urn[]), 'enterpriseUrn'),

				this.invoicingOwnerCollection
					.listBy((this.enterpriseCollection.urns as Urn[]), 'enterpriseUrn'),
			]);
		}

		await this.enterprisePropertyCollection.list();
		await this.enterpriseCountryEnterprisePropertyCollection.listBy(enterpriseIds, 'enterprise');
		const countryEnterprisePropertyIds = this.enterpriseCountryEnterprisePropertyCollection
			.distinctIds('countryEnterpriseProperty');
		await this.countryEnterprisePropertyCollection.listBy(countryEnterprisePropertyIds, 'id');

		if (setForGridInfo) {
			const apeCodeProperty = this.enterprisePropertyCollection.find(ep => ep.reference === 'ape_code');
			const apeCodeIds = this.enterpriseCountryEnterprisePropertyCollection.filter(ecep => {
				return this.countryEnterprisePropertyCollection.some(cep =>
					ecep.getId('countryEnterpriseProperty') === cep.id
					&& cep.getId('enterpriseProperty') === apeCodeProperty?.id
				);
			}).map(ecep => ecep.value);

			const nafCodeProperty = this.enterprisePropertyCollection.find(ep => ep.reference === 'naf_code');
			const nafCodeIds = this.enterpriseCountryEnterprisePropertyCollection.filter(ecep => {
				return this.countryEnterprisePropertyCollection.some(cep =>
					ecep.getId('countryEnterpriseProperty') === cep.id
					&& cep.getId('enterpriseProperty') === nafCodeProperty?.id
				);
			}).map(ecep => ecep.value);

			await Promise.all([
				this.apeCodeCollection.listBy(apeCodeIds),
				this.nafCodeCollection.listBy(nafCodeIds),
			]);
		}

		this.loadedEnterpriseIds = this.idsToStringArray(enterpriseIds);
		if (setForGridInfo) {
			this.loadedEnterpriseForGridInfoIds = this.idsToStringArray(enterpriseIds);
		}
		this.loadingEnterpriseIds = this.loadingEnterpriseIds.filter(id => !this.loadedEnterpriseIds.includes(id.toString()));
		this.loadedCountryEnterprisePropertyIds = this.idsToStringArray(this.countryEnterprisePropertyCollection.ids);
		this.setIsLoading(false);
	}

	public async initOrLoadMoreAsync(enterpriseIds: id[]) {
		if (!this.enterpriseCountryEnterprisePropertyCollection.length && !this.loadingEnterpriseIds.length) {
			return this.initAsync(enterpriseIds, false);
		}

		this.loadingEnterpriseIds = [ ...this.loadingEnterpriseIds, ...this.idsToStringArray(enterpriseIds)];
		this.setIsLoading(true);

		const filteredEnterpriseIds = enterpriseIds
			.filter(enterpriseId => !this.loadedEnterpriseIds.includes(enterpriseId.toString()));

		const collEnterpriseCountryEnterpriseProperty = new ApiCollection(EnterpriseCountryEnterprisePropertyModel);
		await collEnterpriseCountryEnterpriseProperty.listBy(filteredEnterpriseIds, 'enterprise');

		const filteredCepIds = collEnterpriseCountryEnterpriseProperty
			.distinctIds('countryEnterpriseProperty')
			.filter(id => !this.loadedCountryEnterprisePropertyIds.includes(id.toString()));
		const collCountryEnterpriseProperty = new ApiCollection(CountryEnterprisePropertyModel);
		await collCountryEnterpriseProperty.listBy(filteredCepIds, 'id');

		this.enterpriseCountryEnterprisePropertyCollection.set([
			...this.enterpriseCountryEnterprisePropertyCollection.models,
			...collEnterpriseCountryEnterpriseProperty.models,
		]);

		this.countryEnterprisePropertyCollection.set([
			...this.countryEnterprisePropertyCollection.models,
			...collCountryEnterpriseProperty.models,
		]);

		this.loadedEnterpriseIds = [...this.loadedEnterpriseIds, ...this.idsToStringArray(filteredEnterpriseIds)];
		this.loadingEnterpriseIds = this.loadingEnterpriseIds.filter(id => !this.loadedEnterpriseIds.includes(id.toString()));
		this.loadedCountryEnterprisePropertyIds = [ ...this.loadedCountryEnterprisePropertyIds, ...this.idsToStringArray(filteredCepIds)];
		this.setIsLoading(false);
	}

	public isEnterpriseLoadedOrLoading(enterpriseId: id) {
		return this.loadedEnterpriseIds.includes(enterpriseId.toString())
			|| this.loadingEnterpriseIds.includes(enterpriseId.toString());
	}

	public isEnterpriseLoadedOrLoadingForGridInfo(enterpriseId: id) {
		return this.loadedEnterpriseForGridInfoIds.includes(enterpriseId.toString())
			|| this.loadingEnterpriseForGridInfoIds.includes(enterpriseId.toString());
	}

	public async loadForGridInfo(enterpriseIds: id[]) {
		this.setIsLoading(true);

		this.loadingEnterpriseForGridInfoIds = this.idsToStringArray(enterpriseIds);
		const filteredEnterpriseIds = enterpriseIds
			.filter(enterpriseId => !this.loadingEnterpriseForGridInfoIds.includes(enterpriseId.toString()));

		const collEnterprise = new ApiCollection(EnterpriseModel);
		await collEnterprise.listBy(filteredEnterpriseIds, 'id');

		const collEnterpriseConfig = new ApiCollection(EnterpriseConfigModel);
		const collInvoicingOwner = new ApiCollection(InvoicingOwnerModel);

		await Promise.all([
			...collEnterprise.map(async (e) => {
				if (e.getId('enterpriseGroup')) {
					return whenAsync(() => e.enterpriseGroup.isLoaded);
				}
			}),
			collEnterprise.whenIsLoaded(e => e.enterpriseType),
			collInvoicingOwner
				.listBy((collEnterprise.urns as Urn[]), 'enterpriseUrn'),
			collEnterpriseConfig
				.listBy((collEnterprise.urns as Urn[]), 'enterpriseUrn'),
		]);

		this.enterpriseCollection.set([
			...this.enterpriseCollection.models,
			...collEnterprise.models,
		]);

		this.invoicingOwnerCollection.set([
			...this.invoicingOwnerCollection.models,
			...collInvoicingOwner.models,
		]);

		this.enterpriseConfigCollection.set([
			...this.enterpriseConfigCollection.models,
			...collEnterpriseConfig.models,
		]);

		const enterprisesEceps = this.enterpriseCountryEnterprisePropertyCollection
			.filter(ecep => filteredEnterpriseIds.includes(ecep.getId('enterprise')));

		const apeCodeProperty = this.enterprisePropertyCollection.find(ep => ep.reference === 'ape_code');
		const apeCodeIds = enterprisesEceps.filter(ecep => {
			return this.countryEnterprisePropertyCollection.some(cep =>
				ecep.getId('countryEnterpriseProperty') === cep.id
				&& cep.getId('enterpriseProperty') === apeCodeProperty?.id
			);
		}).map(ecep => ecep.value);

		const nafCodeProperty = this.enterprisePropertyCollection.find(ep => ep.reference === 'naf_code');
		const nafCodeIds = enterprisesEceps.filter(ecep => {
			return this.countryEnterprisePropertyCollection.some(cep =>
				ecep.getId('countryEnterpriseProperty') === cep.id
				&& cep.getId('enterpriseProperty') === nafCodeProperty?.id
			);
		}).map(ecep => ecep.value);

		const collApeCode = new ApiCollection(ApeCodeModel);
		const collNafCode = new ApiCollection(NafCodeModel);

		await Promise.all([
			collApeCode.listBy(apeCodeIds),
			collNafCode.listBy(nafCodeIds),
		]);

		this.apeCodeCollection.set([
			...this.apeCodeCollection.models,
			...collApeCode.models,
		]);

		this.nafCodeCollection.set([
			...this.nafCodeCollection.models,
			...collNafCode.models,
		]);

		this.loadedEnterpriseForGridInfoIds = [ ...this.loadedEnterpriseForGridInfoIds, ...this.idsToStringArray(filteredEnterpriseIds)];
		this.loadingEnterpriseForGridInfoIds = this.loadingEnterpriseForGridInfoIds.filter(id => !this.loadedEnterpriseForGridInfoIds.includes(id));
		this.setIsLoading(false);
	}

	private formatValue = (value: string, reference?: DirectoryEnterprisePropertyReference) => {
		switch (reference) {
			case 'siren':
				return formatByInterval(value, 3);
			case 'identification_number':
				return value;
			case 'intra_community_vat':
				return formatByInterval(value, 2);
			case 'naf_code':
				const nafCode = this.nafCodeCollection
					.find(nafCode => nafCode.urn === value);
				return nafCode?.renderName();
			case 'ape_code':
				const typology = this.apeCodeCollection
					.find(nafCode => nafCode.urn === value);
				return typology?.renderName();
			default:
				return value;
		}
	};

	private idsToStringArray(arr: id[]): string[] {
		return arr.map(id => id.toString());
	}
}